// Simple analytics tracking for PriceZing
interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
}

class Analytics {
  private isEnabled = true;

  constructor() {
    // Initialize analytics in production
    if (typeof window !== 'undefined') {
      console.log('Analytics initialized for PriceZing');
    }
  }

  track(event: string, properties?: Record<string, any>) {
    if (!this.isEnabled || typeof window === 'undefined') return;

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties: {
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        ...properties
      }
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics Event:', analyticsEvent);
    }

    // Here you would typically send to your analytics provider
    // Example integrations:
    
    // Google Analytics 4
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', event, properties);
    }

    // Facebook Pixel
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', event, properties);
    }

    // LinkedIn Insight Tag
    if (typeof window.lintrk !== 'undefined') {
      window.lintrk('track', { conversion_id: event });
    }
  }

  page(pageName?: string) {
    this.track('page_view', {
      page_name: pageName || document.title,
      page_path: window.location.pathname
    });
  }

  // Lead generation tracking
  trackLeadGeneration(leadType: 'form_submission' | 'demo_request' | 'consultation_booking', data?: Record<string, any>) {
    this.track('lead_generated', {
      lead_type: leadType,
      ...data
    });
  }

  // Business specific events
  trackPricingConsultationRequest(formData?: Record<string, any>) {
    this.track('consultation_requested', {
      service_type: 'pricing_consultation',
      ...formData
    });
  }

  trackDemoRequest(platform: 'smart_pricing' | 'general', formData?: Record<string, any>) {
    this.track('demo_requested', {
      platform,
      ...formData
    });
  }

  trackNewsletterSubscription(email?: string) {
    this.track('newsletter_subscription', {
      email_provided: !!email
    });
  }

  trackDownload(resourceType: string, resourceName?: string) {
    this.track('resource_downloaded', {
      resource_type: resourceType,
      resource_name: resourceName
    });
  }

  // Engagement tracking
  trackButtonClick(buttonName: string, location?: string) {
    this.track('button_clicked', {
      button_name: buttonName,
      button_location: location
    });
  }

  trackLanguageChange(fromLanguage: string, toLanguage: string) {
    this.track('language_changed', {
      from_language: fromLanguage,
      to_language: toLanguage
    });
  }

  // E-commerce style events for services
  trackServiceViewed(serviceName: string) {
    this.track('service_viewed', {
      service_name: serviceName
    });
  }

  trackContactAttempt(method: 'phone' | 'email' | 'whatsapp' | 'form') {
    this.track('contact_attempted', {
      contact_method: method
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Global type declarations for external scripts
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    lintrk?: (...args: any[]) => void;
  }
}